import React from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NorreButton } from "components/common/NorreButton";
import { NorreButtonSecondary } from "components/common/NorreButtonSecondary";
import { columnFlex, dFlex, gap2 } from "styles/sx";
import { hasValidToken } from "Entity/Authentication";
import { handleLogoutWithoutHook } from "Entity/LogoutHandler";
import { setAuthLogoutFlash } from "Utils/graphqlAuthErrors";
import { displayError } from "Utils/errors";
import { resetFunctionalityRefreshFlag } from "Entity/Functionality/BackendFunctionality";
import { NorreLogo } from "components/common/NorreLogo";

interface Props {
    error: unknown;
    resetError: () => void;
    /** True when rendered inside the login-required part of the app. */
    loginRequiredArea?: boolean;
}

/**
 * Fallback UI for the top level error boundaries. Must not use router or
 * app context hooks: the boundary sits above those providers and the tree
 * below it has just crashed.
 *
 * In the login-required area the user is directed to log in again only when
 * both the access and the refresh token have expired (hasValidToken checks
 * both). An expired access token alone is refreshed silently on the next
 * request, so it is not a reason to log out.
 */
export const AppErrorFallback = ({
    error,
    resetError,
    loginRequiredArea = false,
}: Props) => {
    const { t } = useTranslation();
    const sessionEnded = loginRequiredArea && !hasValidToken();

    const retry = () => {
        // Let FunctionalityRefresher re-fetch permissions on remount; stale
        // cached permissions are a common cause of render errors.
        resetFunctionalityRefreshFlag();
        resetError();
    };

    const logoutAndLoginAgain = async () => {
        setAuthLogoutFlash();
        // Clears all app storage keys, the Apollo store, ends the server
        // session when tokens are still valid, and reloads to the login page.
        await handleLogoutWithoutHook();
    };

    return (
        <Box
            sx={{
                ...columnFlex,
                ...gap2,
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                p: 2,
            }}
        >
            <NorreLogo
                sx={{
                    width: "180px",
                }}
            />
            <Alert severity="error">
                <Typography>
                    {sessionEnded
                        ? t("session_ended_relogin")
                        : t("unexpected_error_occurred")}
                </Typography>
                {!sessionEnded && error instanceof Error && (
                    <Typography variant="body2">
                        {displayError(error)}
                    </Typography>
                )}
            </Alert>
            <Box sx={{ ...dFlex, ...gap2 }}>
                {sessionEnded ? (
                    <NorreButton
                        variant="outlined"
                        onClick={logoutAndLoginAgain}
                    >
                        {t("login_again")}
                    </NorreButton>
                ) : (
                    <>
                        <NorreButton variant="outlined" onClick={retry}>
                            {t("retry")}
                        </NorreButton>
                        {loginRequiredArea ? (
                            <NorreButtonSecondary onClick={logoutAndLoginAgain}>
                                {t("logout_and_login_again")}
                            </NorreButtonSecondary>
                        ) : (
                            <NorreButtonSecondary
                                onClick={() => window.location.reload()}
                            >
                                {t("reload_page")}
                            </NorreButtonSecondary>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};
